// Self-serve subscription plans for the Agency Tools product.
//
// Renders on the Lead-Gen Tools marketing landing page, right before the
// existing "Talk to us" PricingTease section. The self-serve tiers get a
// card each (Free / Starter / Growth / Scale / Lifetime on /pricing).
// Monthly / Yearly toggle drives the price + a "2 months free" savings
// badge on yearly cards. Lifetime is a one-time plan: it ignores the
// toggle and renders a one-time price, never "/mo".
//
// Pricing is authoritative — must mirror
// `supabase/functions/_shared/razorpay-agency-plans.ts` (the server-side
// source of truth for what Razorpay actually charges).

import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Check, Info, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { openEmbedInquiry } from './openEmbedInquiry';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { stashPricingIntent } from './pricingIntent';
import { TRIAL_DASHBOARD_URL, buildTrialWelcomePath } from './checkoutUrl';
import { useUserPlanState } from '@/hooks/useUserPlanState';
import { type Cycle, type TierCard, YEARLY_ENABLED, TIERS, formatPrice } from './agencyTiers';

// What a scan buys, shown on every plan behind an info icon.
//
// Framed as what the number covers rather than what it costs. The one tool
// that is not 1 scan gets its reason attached in the same breath, because a
// bare "7" invites the reader to assume they are being penalised. Told
// plainly, it reads as more work done per run, which is what it is.
function ScanExplainer() {
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger
          type="button"
          aria-label="How scans work"
          className="ml-1.5 inline-flex align-middle text-white/35 transition hover:text-white/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40 rounded-full"
        >
          <Info className="size-3.5" />
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-[260px] text-left leading-relaxed">
          <p className="font-medium">One scan, one audit.</p>
          <p className="mt-1 text-muted-foreground">
            Every tool uses a single scan each time you run it, so your monthly
            number is simply how many audits you get.
          </p>
          <p className="mt-1.5 text-muted-foreground">
            The exception is AI Search Visibility, at 7 scans. It checks how you
            show up across several AI models in one pass, so a single run does
            the work of several.
          </p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// Bullets for the standalone Free card (only rendered when showFreeCard).
// Mirrors the free-grant terms: 1,500 non-expiring credits, no card. Kept
// here (not in agencyTiers.TIERS) because the Free card is a special-cased
// card with its own claim/dashboard/already-claimed states, not a paid tier.
// Deliberately says nothing about ClinicLeads. Signing up does also grant 50
// healthcare screening scans, and that bullet used to sit here, but this is the
// agency audit-tools pricing page and ClinicLeads has its own. Two products'
// offers on one card made the free tier read as a bundle nobody was actually
// choosing between.
const FREE_HIGHLIGHTS = [
  '25 scans, free for 14 days',
  'All audit tools unlocked',
  'No card required',
  'Email + dashboard delivery',
  'Your branding on every report',
];

// Public marketing pages render outside the AuthProvider tree, so we read
// the session straight from the supabase client instead of useAuth(). For
// sign-in we call signInWithOAuth directly — the public page only ever
// runs on the main domain (not on whitelabel agency subdomains), so the
// agency-domain branch of AuthProvider.signInWithGoogle is not relevant here.

export function AgencyPricingPlans({ showHeader = true, showTrialCta = true, showFreeCard = false }: { showHeader?: boolean; showTrialCta?: boolean; showFreeCard?: boolean } = {}) {
  const [cycle, setCycle] = useState<Cycle>('monthly');
  const navigate = useNavigate();
  const [isSignedIn, setIsSignedIn] = useState(false);

  // Drives the standalone Free card's three states. 'unauthenticated' and
  // 'no_agency' both mean the free 1,500 credits have not been claimed yet
  // (a signed-in no_agency user simply has not provisioned an agency); every
  // other state means the user already has an agency, so they have claimed.
  const planState = useUserPlanState();
  const freeClaimed = !['unauthenticated', 'loading', 'no_agency'].includes(planState.state);
  const planLoading = planState.state === 'loading';

  useEffect(() => {
    let cancelled = false;
    supabase.auth.getSession().then(({ data }) => {
      if (!cancelled) setIsSignedIn(!!data.session?.user);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setIsSignedIn(!!session?.user);
    });
    return () => { cancelled = true; subscription.unsubscribe(); };
  }, []);

  const startGoogleOAuth = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/fly-app`,
        queryParams: { access_type: 'offline', prompt: 'select_account' },
        scopes: 'email profile https://www.googleapis.com/auth/business.manage',
      },
    });
    if (error) console.error('Google sign-in failed:', error);
  };

  // Re-read the live session before deciding to OAuth. The `isSignedIn`
  // state can lag the real session (initial getSession still in flight, a
  // transient onAuthStateChange null, or a fast click), which would bounce
  // an already signed-in visitor through Google again instead of taking
  // them straight to checkout. getSession reads the persisted session
  // locally, so this is cheap.
  const resolveSignedIn = async (): Promise<boolean> => {
    if (isSignedIn) return true;
    const { data } = await supabase.auth.getSession();
    return !!data.session?.user;
  };

  type BusyKey = TierCard['slug'] | 'trial';
  const [busyTier, setBusyTier] = useState<BusyKey | null>(null);

  // Routes the user into the 7-day free trial flow. The trial is no
  // longer a separate "Free" tile — it lives as a secondary CTA on the
  // Starter card. Signed-in users get their lead-gen agency provisioned
  // (the bootstrap edge function is idempotent for returning users) and
  // land on the lead-gen tools dashboard. Signed-out users stash the
  // intent + start Google OAuth; AuthProvider on /fly-app consumes the
  // intent on return and runs bootstrapAndRedirectForIntent which
  // ultimately lands them on the same /lead-gen-tools?welcome=free URL.
  const onTrialClick = async () => {
    if (await resolveSignedIn()) {
      setBusyTier('trial');
      try {
        // intent='trial_later' — the "Try free for 7 days" CTA now defers
        // the trial: bootstrap strips the auto-trial, the user lands on the
        // dashboard and starts the 7 days themselves so a stale login never
        // burns trial days before they arrive.
        const { data } = await supabase.functions.invoke('agency-public-signup-bootstrap', { body: { intent: 'trial_later' } });
        // A signed-in visitor who has never had an agency is still a brand-new
        // account, so send them through the welcome step, the same as the
        // post-OAuth path does. Landing them straight on the dashboard means
        // the non-dismissible ProfileCompletionGate opens over it with the
        // product tour on top, which is the stack this avoids.
        if (data?.created === true && data?.agency_id) {
          setBusyTier(null);
          navigate(buildTrialWelcomePath(data.agency_id as string));
          return;
        }
      } catch (err) {
        console.warn('Bootstrap failed; AuthProvider will retry:', err);
      }
      setBusyTier(null);
      navigate(TRIAL_DASHBOARD_URL);
      return;
    }
    stashPricingIntent({ kind: 'free' });
    startGoogleOAuth();
  };

  // Billing cycle to carry into checkout for a given card. One-time plans
  // (Lifetime) are charged once, so they send 'none' instead of the
  // monthly/yearly toggle value. 'none' is what a non-recurring purchase
  // carries end to end (pricing intent -> checkout URL -> billing_cycle),
  // so sending 'monthly' here would describe the plan as recurring.
  const cycleForTier = (tier: TierCard): 'monthly' | 'yearly' | 'none' =>
    tier.cycle === 'one_time' ? 'none' : cycle;

  const onCtaClick = async (tier: TierCard) => {
    const tierCycle = cycleForTier(tier);
    if (await resolveSignedIn()) {
      // Paid: bootstrap to get the agency id, then navigate with it baked in.
      // intent='paid' — strips the auto-trial so the buyer reaches checkout
      // with no active subscription. Razorpay's mandate flow creates the
      // legit trial-then-paid subscription after card authentication.
      setBusyTier(tier.slug);
      // plan names the buyer (P1): no "free trial" welcome while they pay.
      const { data, error } = await supabase.functions.invoke('agency-public-signup-bootstrap', { body: { intent: 'paid', plan: tier.slug } });
      setBusyTier(null);
      if (error || !data?.agency_id) {
        console.error('Bootstrap failed:', error, data);
        // Fall back to opening the inquiry modal so the user has a path forward.
        openEmbedInquiry(`pricing-${tier.slug}-bootstrap-failed`, 'talk');
        return;
      }
      const qs = new URLSearchParams({
        plan: tier.slug,
        cycle: tierCycle,
        agency: data.agency_id as string,
        source: `pricing-${tier.slug}-${tierCycle}`,
      });
      navigate(`/fly-app/agency-checkout?${qs.toString()}`);
      return;
    }
    // Signed out: stash the intent + start Google OAuth. AuthProvider on
    // /fly-app consumes the intent on return and routes us correctly.
    // No cast: PendingPricingIntent's plan union now matches TierCard['slug']
    // exactly, so a new tier can never be stashed as an unrecognised plan and
    // silently dropped by parseIntent on the way back from OAuth.
    stashPricingIntent({ kind: 'paid', plan: tier.slug, cycle: tierCycle });
    startGoogleOAuth();
  };

  return (
    <section className="relative bg-gradient-to-b from-[#0d0d14] to-gray-950 py-32 lg:py-40 overflow-hidden">
      {/* Subtle gradient backdrop for depth */}
      <div
        className="absolute inset-0 opacity-30 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse at top, rgba(139, 92, 246, 0.15), transparent 50%), radial-gradient(ellipse at bottom right, rgba(236, 72, 153, 0.08), transparent 50%)',
        }}
      />

      <div className="relative container mx-auto px-6 lg:px-8">
        <motion.div
          id="pricing"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl mx-auto text-center mb-12 scroll-mt-28"
        >
          {showHeader && (
            <>
              <div className="text-xs uppercase tracking-[0.2em] text-fuchsia-400 font-semibold mb-4">
                Self-serve plans
              </div>
              <h2
                className="text-white font-bold tracking-tight"
                style={{ fontSize: 'clamp(2rem, 4vw, 3.4rem)', lineHeight: 1.1 }}
              >
                Pricing that scales with your lead flow.
              </h2>
              <p className="mt-5 text-white/60 text-lg">
                One scan per audit, whatever the tool.{YEARLY_ENABLED && ' Yearly billing gets you 2 months free.'} Cancel anytime.
              </p>
            </>
          )}

          {/* Monthly / Yearly toggle (hidden while YEARLY_ENABLED is false) */}
          {YEARLY_ENABLED && (
          <div className="mt-8 inline-flex items-center rounded-full border border-white/15 bg-white/[0.04] p-1">
            <button
              type="button"
              onClick={() => setCycle('monthly')}
              className={`px-5 py-2 text-sm rounded-full transition ${
                cycle === 'monthly'
                  ? 'bg-white text-gray-900 font-semibold'
                  : 'text-white/70 hover:text-white'
              }`}
            >
              Monthly
            </button>
            <button
              type="button"
              onClick={() => setCycle('yearly')}
              className={`relative px-5 py-2 text-sm rounded-full transition ${
                cycle === 'yearly'
                  ? 'bg-white text-gray-900 font-semibold'
                  : 'text-white/70 hover:text-white'
              }`}
            >
              Yearly
              <span className="ml-1.5 inline-flex items-center gap-1 rounded-full bg-emerald-500/20 text-emerald-300 text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5">
                2 mo free
              </span>
            </button>
          </div>
          )}
        </motion.div>

        {/* Card grid. The count depends on showFreeCard:
              showFreeCard  -> 5 cards (Free + Starter/Growth/Scale/Lifetime)
              default       -> 4 cards (the TIERS array only)
            The standalone "Free / 7-day trial" tile has been collapsed onto
            Starter via the trialCtaLabel field, so buyers see "Try free for 7
            days" as a secondary CTA on the Starter card instead of a phantom
            Free plan no one subscribes to.

            Column counts, and why:
              5 cards: 1 (base) / 2 (md, 2+2+1) / 3 (lg, 3+2) / 5 (xl, one row)
              4 cards: 1 (base) / 2 (md, 2+2)   / 4 (lg, one row)
            The old lg:grid-cols-4 would have left the fifth card alone on a
            second row at lg, which reads as a mistake. lg:grid-cols-3 gives a
            3+2 split instead, and the single row only returns at xl.
            5 across is held back to xl on purpose. The Tailwind container here
            is full width below 1400px, so at lg it is ~960px after px-8: five
            cards would get ~176px each. At xl the same maths gives ~227px, and
            ~240px once max-w-7xl binds from 1400px up, which matches the card
            width this page already ships (4 cards on ~960px at lg today).
            At md the fifth card does wrap onto a row of its own, which is just
            how an odd count stacks in two columns. */}
        {/* Centred, and sized to the number of cards actually rendered.
            This used to be lg:grid-cols-4 (or 5 with the Free card) because
            there were four paid tiers plus Lifetime. There are now two, so a
            four-column track would stretch two cards across the full width and
            leave them hugging the left. max-w is set to roughly the natural
            width of the cards on show, and mx-auto centres what is left. */}
        <div className={`grid gap-5 justify-center md:grid-cols-2 ${showFreeCard ? 'lg:grid-cols-3 max-w-5xl' : 'max-w-3xl'} mx-auto items-stretch`}>
          {/* Standalone Free card. Replaces the "Claim 1,500 free scans"
              secondary CTA that used to sit on the Starter card (the pricing
              page passes showTrialCta={false} so that button is gone here).
              Three states driven by useUserPlanState:
                - not claimed (signed out OR signed in with no agency) -> the
                  claim CTA runs the existing onTrialClick flow (signup for
                  signed-out; provision + dashboard for signed-in no_agency).
                - already claimed (any agency plan state) -> "Already claimed",
                  which just takes them to the dashboard.
              No backend change: this reuses onTrialClick verbatim. */}
          {showFreeCard && (
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.55 }}
              className="relative"
            >
              <div className="h-full rounded-2xl border p-6 flex flex-col items-center text-center transition-all duration-300 hover:-translate-y-1 border-white/10 bg-gradient-to-br from-[#13131c] to-[#0c0c14] hover:border-white/20">
                <div className="mb-5">
                  <h3 className="text-white text-xl font-bold mb-1">Free</h3>
                  <p className="text-white/55 text-sm leading-snug">Start free, no card</p>
                </div>

                <div className="mb-5 min-h-[60px]">
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="text-white text-4xl font-bold tabular-nums">$0</span>
                    <span className="text-white/55 text-sm">/mo</span>
                  </div>
                </div>

                <ul className="space-y-2.5 mb-6 flex-1 w-full">
                  {FREE_HIGHLIGHTS.map((h, idx) => (
                    <li
                      key={h}
                      className="flex items-start justify-center gap-2 text-sm text-white/85"
                    >
                      {idx === 0 ? (
                        <Sparkles className="mt-0.5 size-3.5 text-violet-300 shrink-0" />
                      ) : (
                        <Check className="mt-0.5 size-3.5 text-emerald-400 shrink-0" />
                      )}
                      {/* No sub-label. This used to translate the scan
                          figure into runs, the same way the paid cards did.
                          Once the bullet itself became "25 scans, free for 14
                          days" the sub-label said exactly the same sentence
                          again, twice on one card. */}
                      <span className="text-left">
                        {h}
                        {idx === 0 && <ScanExplainer />}
                      </span>
                    </li>
                  ))}
                </ul>

                <div className="space-y-2 mt-auto w-full">
                  {freeClaimed ? (
                    <Button
                      type="button"
                      onClick={() => navigate('/fly-app/lead-gen-tools')}
                      className="w-full h-11 rounded-xl font-semibold bg-white/10 hover:bg-white/[0.16] text-white/80 border border-white/15"
                    >
                      <Check className="mr-1.5 size-4 text-emerald-400" />
                      Already claimed
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      onClick={onTrialClick}
                      disabled={planLoading || busyTier === 'trial'}
                      className="w-full h-11 rounded-xl font-semibold bg-white/90 hover:bg-white text-gray-900"
                    >
                      {busyTier === 'trial' ? 'Setting up...' : 'Start 14-day free trial'}
                      <ArrowRight className="ml-1.5 size-4" />
                    </Button>
                  )}
                </div>
              </div>
            </motion.div>
          )}
          {TIERS.map((t, i) => {
            // One-time plans (Lifetime) are charged once: they ignore the
            // monthly/yearly toggle, read one_time_usd, and get a one-time
            // price suffix. Without this branch a $99 one-time card would
            // render "$99/mo" on a public page, which misstates the price.
            const isOneTime = t.cycle === 'one_time';
            // Yearly paid cards lead with the per-month equivalent (smaller
            // headline number) and disclose the true annual charge below.
            const headline = isOneTime
              ? t.one_time_usd ?? null
              : cycle === 'monthly' ? t.monthly_usd
              : t.yearly_usd != null ? Math.round(t.yearly_usd / 12) : null;
            // Per-card price suffix. Was a hardcoded '/mo' string.
            const priceSuffix = isOneTime ? 'one time' : '/mo';
            const showYearlyNote = !isOneTime && cycle === 'yearly' && t.yearly_usd != null;
            return (
              <motion.div
                key={t.slug}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.55, delay: i * 0.06 }}
                className={`relative ${t.highlight ? 'lg:-my-3 z-10' : ''}`}
              >
                {t.highlight && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10 px-3 py-1 rounded-full bg-fuchsia-700 text-white text-xs font-semibold">
                    Most popular
                  </div>
                )}
                <div
                  className={`h-full rounded-2xl border p-6 flex flex-col items-center text-center transition-all duration-300 hover:-translate-y-1 ${
                    t.highlight
                      ? 'border-fuchsia-500/50 bg-gradient-to-br from-fuchsia-500/[0.12] via-[#15131f] to-[#0c0c14] shadow-2xl shadow-black/40 ring-1 ring-fuchsia-400/20 lg:p-7'
                      : 'border-white/10 bg-gradient-to-br from-[#13131c] to-[#0c0c14] hover:border-white/20'
                  }`}
                >
                  <div className="mb-5">
                    <h3 className="text-white text-xl font-bold mb-1">{t.name}</h3>
                    <p className="text-white/55 text-sm leading-snug">{t.tagline}</p>
                  </div>

                  <div className="mb-5 min-h-[60px]">
                    <div className="flex items-baseline justify-center gap-1">
                      <span className="text-white text-4xl font-bold tabular-nums">{formatPrice(headline)}</span>
                      <span className="text-white/55 text-sm">{priceSuffix}</span>
                    </div>
                    {showYearlyNote && (
                      <div className="text-emerald-300/90 text-xs mt-1.5">
                        billed yearly at ${t.yearly_usd}
                      </div>
                    )}
                  </div>
                  <ul className="space-y-2.5 mb-6 flex-1 w-full">
                    {t.highlights.map((h, idx) => (
                      <li
                        key={h}
                        className="flex items-start justify-center gap-2 text-sm text-white/85"
                      >
                        {idx === 0 ? (
                          <Sparkles className="mt-0.5 size-3.5 text-violet-300 shrink-0" />
                        ) : (
                          <Check className="mt-0.5 size-3.5 text-emerald-400 shrink-0" />
                        )}
                        {/* The allowance bullet used to carry a sub-label
                            translating scans into runs ("5,000 scans" ->
                            "about 45 profile audits"). Under scan pricing the
                            bullet already IS the run count, so the sub-label
                            just restated it: "100 scans per month / about 100
                            scans". Removed rather than reworded. */}
                        <span className="text-left">
                          {h}
                          {idx === 0 && <ScanExplainer />}
                        </span>
                      </li>
                    ))}
                  </ul>

                  <div className="space-y-2 mt-auto w-full">
                    <Button
                      onClick={() => onCtaClick(t)}
                      disabled={busyTier === t.slug || busyTier === 'trial'}
                      className={`w-full h-11 rounded-xl font-semibold ${
                        t.highlight
                          ? 'bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-500 hover:to-fuchsia-500 text-white'
                          : 'bg-white/90 hover:bg-white text-gray-900'
                      }`}
                    >
                      {busyTier === t.slug ? 'Setting up...' : t.ctaLabel}
                      <ArrowRight className="ml-1.5 size-4" />
                    </Button>
                    {/* Secondary trial CTA. Only Starter carries this
                        today — the trial is "try Starter free for 7
                        days" semantically. Renders as an outlined
                        button under the primary so the buyer sees both
                        the commit path AND the no-risk path side by
                        side without a second tile. */}
                    {showTrialCta && t.trialCtaLabel && (
                      <Button
                        type="button"
                        onClick={onTrialClick}
                        disabled={busyTier === t.slug || busyTier === 'trial'}
                        variant="outline"
                        className="w-full h-10 rounded-xl font-medium bg-transparent border border-white/20 text-white/85 hover:bg-white/10 hover:text-white hover:border-white/35"
                      >
                        {busyTier === 'trial' ? 'Setting up...' : t.trialCtaLabel}
                      </Button>
                    )}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        <p className="mt-10 text-center text-xs text-white/45 max-w-2xl mx-auto leading-relaxed">
          Prices shown in USD. Indian agencies are billed in INR at a locked
          ₹83/$1 with a GST-compliant tax invoice. Cancel anytime.
        </p>
      </div>
    </section>
  );
}
